import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Modal, ModalHeader, ModalBody } from '@evertrue/et-components';
import EverTrue from 'app';
import Api from 'entities/helpers/api';
import BulkMessageStepIndicator from './bulk-message-step-indicator';
import BulkMessageContentStep from './bulk-message-content-step';
import BulkMessageAudienceStep from './bulk-message-audience-step';
import BulkMessageScheduleStep from './bulk-message-schedule-step';
import useCadence from '../../hooks/use-cadence';
import useCadenceMutations from '../../hooks/use-cadence-mutations';
import useTemplates from '../../hooks/use-templates';
import cadenceGqlCall from '../../utils/cadence-gql-call';

import {
  CADENCE_QUERY,
  ORG_TAGS_QUERY,
  REGISTER_TAG_MUTATION,
  CREATE_CADENCE_WITH_CONFIG_MUTATION,
  UPDATE_CADENCE_WITH_CONFIG_MUTATION,
} from '../../utils/cadence-queries';
import { findIncompleteConfigs } from '../../utils/config-completion';
import { useEmailTemplatesProvider } from 'apps/outreach/email/templates/email-templates-context';
import { useEmailSignaturesProvider } from 'apps/outreach/email/signatures/email-signatures-context';
import TemplatePickerModal from '../email-template/template-picker-modal';
import SignaturePickerModal from '../email-template/signature-picker-modal';
import useFileUploader from 'components/emailer/hooks/use-file-uploader';
import { filesToEventConfigAttachments, eventConfigAttachmentsToUploader } from '../../utils/cadence-attachments';
import { buildAttachmentRequests } from 'components/emailer/form/email-schema';
import useInteractionTypes from '../../hooks/use-interaction-types';
import usePortfolioTeams from '../../hooks/use-portfolio-teams';
import { useIdentity } from 'base/identity-resolver';
import useLists from '../../hooks/use-lists';
import useSavedSearches from '../../hooks/use-saved-searches';
import useContactSearch from '../../hooks/use-contact-search';
import useProposals from '../../hooks/use-proposals';
import useProposalLabels from '../../hooks/use-proposal-labels';
import useDebounce from 'hooks/use-debounce';
import { SELECTION_METHOD_OPTIONS } from '../../utils/cadence-constants';
import { fetchUsersById } from 'apps/volunteers/vms-queries';
import useDomainStatus from '../../hooks/use-domain-status';

/**
 * Create Bulk Message page with 3-step wizard.
 * Step 1: Message Content, Step 2: Audience & Metrics, Step 3: Schedule & Launch
 * Updated to match PM mockup design.
 */
const CreateBulkMessagePage = ({ cadenceId = null, preSelectedShellId = null }) => {
  const { cadence: existingCadence, loading: audiencesLoading, refetch: refetchCadence } = useCadence(cadenceId);
  const latestVersionRef = useRef(null);
  const getVersion = () => latestVersionRef.current ?? existingCadence?.version;
  const trackVersion = (v) => {
    if (v != null) latestVersionRef.current = v;
  };
  const {
    bulkEnrollMembers,
    createCadenceListAudience,
    createCadenceAudience,
    saveIndividualCadenceAudiences,
    deleteCadenceAudience,
    fetchCadenceSharingSettings,
  } = useCadenceMutations();
  const savedSearches = useSavedSearches();
  const { interactionTypes } = useInteractionTypes();
  const portfolioTeams = usePortfolioTeams();
  const contactLists = useLists();
  const { templates: outreachTemplates } = useEmailTemplatesProvider();
  const { signatures } = useEmailSignaturesProvider();
  const { files, removeFile, onImageUpload, onFileUpload, loadAttachments } = useFileUploader();
  const { user } = useIdentity();
  const { templates: cadenceShells } = useTemplates();
  // Search-as-you-type against the backend (NEXDEV-2120) — a fixed unfiltered
  // browse window can never cover every org's proposals, so typing must
  // query the server rather than only filtering what's already loaded.
  const [proposalSearch, setProposalSearch] = useState('');
  const debouncedProposalSearch = useDebounce(proposalSearch, 300);
  const proposals = useProposals(debouncedProposalSearch);
  const { status: domainStatus, loading: domainLoading, domain: orgDomainObj } = useDomainStatus();
  const isOrgAdmin = !!(EverTrue.store?.user?.isSuperUser?.() || EverTrue.store?.user?.isOwner?.());
  const bulkDefaultName = 'Untitled Bulk Message';
  const [currentStep, setCurrentStep] = useState(1);
  const [messageName, setMessageName] = useState(bulkDefaultName);
  const [isEditingName, setIsEditingName] = useState(false);
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [namePromptValue, setNamePromptValue] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  // Step 1: Message Content
  const [senderName, setSenderName] = useState(() => {
    const u = user || {};
    const raw = u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || '';
    return raw.replace(/(?:^|[\s.])(\w)/g, (m, c) => m.replace(c, c.toUpperCase()));
  });
  // Use the org's verified sending domain, not the user's personal email domain.
  const orgDomain = orgDomainObj?.name || 'example.com';
  const [senderEmailPrefix, setSenderEmailPrefix] = useState('');
  const [replyToEmail, setReplyToEmail] = useState(() => {
    return user?.email || '';
  });
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [senderExpanded, setSenderExpanded] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(true);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [appliedTemplate, setAppliedTemplate] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [appliedSignature, setAppliedSignature] = useState(null);

  // Step 2: Audience & Metrics
  const [selectionMethod, setSelectionMethod] = useState(SELECTION_METHOD_OPTIONS[0]);
  const [selectedList, setSelectedList] = useState(null);
  const [selectedSavedSearch, setSelectedSavedSearch] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [successMetric, setSuccessMetric] = useState(null);

  // Step 2: Advanced Settings
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [respectBlackoutDates, setRespectBlackoutDates] = useState(false);
  const [isSharingOpen, setIsSharingOpen] = useState(false);
  const [isProposalsOpen, setIsProposalsOpen] = useState(false);
  const [isBlueprintOpen, setIsBlueprintOpen] = useState(false);
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const [isBlackoutOpen, setIsBlackoutOpen] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]); // [{userId, permission: 'view'|'edit', label, shareId?}]
  const [, setOriginalSharedUsers] = useState([]);
  const [pendingShareUsers, setPendingShareUsers] = useState([]); // users selected in picker before adding
  const [pendingSharePermission, setPendingSharePermission] = useState('view');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [linkedProposals, setLinkedProposals] = useState([]);
  // Hydration (from an existing cadence or a blueprint shell, below) restores
  // linked proposals with a "Proposal #<id>" placeholder label before the
  // real name is known. Resolve it here — against the browse list first,
  // then a targeted fetch for anything outside it — and correct the label
  // once it's available (NEXDEV-2120).
  const resolvedProposalLabels = useProposalLabels(
    linkedProposals.map((p) => p.value),
    proposals
  );
  useEffect(() => {
    setLinkedProposals((prev) =>
      prev.map((p) =>
        resolvedProposalLabels[p.value] && resolvedProposalLabels[p.value] !== p.label
          ? { ...p, label: resolvedProposalLabels[p.value] }
          : p
      )
    );
  }, [resolvedProposalLabels]);
  const [cadenceTemplate, setCadenceTemplate] = useState(preSelectedShellId ? String(preSelectedShellId) : '');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);

  // Resolve blueprint name when selected via prop or restored from existing cadence
  useEffect(() => {
    if (cadenceTemplate && cadenceShells?.length > 0 && !templateSearch) {
      const match = cadenceShells.find((t) => String(t.id) === String(cadenceTemplate));
      if (match) setTemplateSearch(match.name);
    }
  }, [cadenceTemplate, cadenceShells, templateSearch]);

  // Step 3: Schedule
  const testContactSearch = useContactSearch();
  const [testRecipient, setTestRecipient] = useState(null);
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);

  // Shell (blueprint) mode: track whether blueprint content has been loaded
  const [shellLoaded, setShellLoaded] = useState(false);

  // Edit mode: pre-populate from existing cadence
  const [prePopulated, setPrePopulated] = useState(false);
  useEffect(() => {
    if (!existingCadence || prePopulated) return;
    latestVersionRef.current = existingCadence.version;
    setMessageName(existingCadence.name || 'Untitled Bulk Message');
    const emailEvent = (existingCadence.events || []).find((e) => (e.eventType || '').toLowerCase() === 'email');
    // Prefer cadence-level sender fields; fall back to email event config for older records.
    const fromEmail = existingCadence.senderEmail || emailEvent?.eventConfig?.from_email;
    const fromName = existingCadence.senderName || emailEvent?.eventConfig?.from_name;
    const ecRT = emailEvent?.eventConfig;
    const replyTo =
      existingCadence.replyToEmail ||
      (Array.isArray(ecRT?.reply_to_emails) ? ecRT.reply_to_emails.join(', ') : null) ||
      ecRT?.reply_to_email ||
      ecRT?.reply_to;
    if (fromName) setSenderName(fromName);
    if (fromEmail) {
      const atIdx = fromEmail.indexOf('@');
      setSenderEmailPrefix(atIdx >= 0 ? fromEmail.slice(0, atIdx) : fromEmail);
    }
    if (replyTo) setReplyToEmail(replyTo);
    if (emailEvent?.eventConfig) {
      const config = emailEvent.eventConfig;
      if (config.subject) setSubject(config.subject);
      if (config.html_body) setBodyText(config.html_body);
      if (config.signature_id) {
        setAppliedSignature({ id: config.signature_id, signature_name: config.signature_name || 'Signature' });
      }
      if (config.template_name || emailEvent.emailTemplateId) {
        setAppliedTemplate({ id: emailEvent.emailTemplateId, templateName: config.template_name || 'Template' });
      }
      // Only rehydrate from the server when the uploader is empty (a genuine
      // edit-load). After a save transitions this page into edit mode, the
      // files are already present locally — re-loading would re-fetch and
      // duplicate them.
      if (config.attachments?.length && files.length === 0) {
        loadAttachments(eventConfigAttachmentsToUploader(config.attachments));
      }
    }
    if (existingCadence.tags?.length > 0) setTags(existingCadence.tags);

    // Restore linked proposals
    if (existingCadence.linkedProposals?.length > 0) {
      const restored = existingCadence.linkedProposals.map((lp) => ({
        value: String(lp.proposalId),
        label: `Proposal #${lp.proposalId}`,
      }));
      setLinkedProposals(restored);
    }

    // Restore blueprint association (name resolved by the shell-name effect below)
    if (existingCadence.sourceCadenceTemplateId && !preSelectedShellId) {
      setCadenceTemplate(String(existingCadence.sourceCadenceTemplateId));
    }

    // Restore success metric (Gap 2)
    // Backend stores config keys in snake_case but the UI components read camelCase
    if (existingCadence.successMetrics?.length > 0) {
      const m = existingCadence.successMetrics[0];
      const raw = m.metricConfig || {};
      const config = {
        ...raw,
        teamId: raw.teamId || raw.team_id || null,
        teamName: raw.teamName || raw.team_name || null,
        stageId: raw.stageId || raw.stage_id || null,
        stageName: raw.stageName || raw.stage_name || null,
        savedSearchId: raw.savedSearchId || raw.saved_search_id || null,
        savedSearchName: raw.savedSearchName || raw.saved_search_name || null,
        savedSearchListId: raw.savedSearchListId ?? raw.saved_search_list_id ?? null,
        interactionType: raw.interactionType || raw.interaction_type || null,
        minAmount: raw.minAmount || raw.min_amount || null,
      };
      setSuccessMetric({
        id: m.id,
        type: m.metricType,
        targetPercentage: m.targetPercentage || 0,
        config,
      });
    }

    // Restore scheduled send date (Gap 3)
    if (existingCadence.scheduledSendDate) {
      // The backend stores scheduled send times as naive UTC strings (no Z suffix).
      // Append Z so the browser parses as UTC, then getHours/getDate give local time.
      const raw = existingCadence.scheduledSendDate;
      const utcStr = raw.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(raw) ? raw : raw + 'Z';
      const dt = new Date(utcStr);
      if (!isNaN(dt.getTime())) {
        setScheduleType('later');
        // Format as YYYY-MM-DD for the date input (local time)
        const year = dt.getFullYear();
        const month = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        setScheduleDate(`${year}-${month}-${day}`);
        // Format as HH:MM for the time input (local time)
        const hours = String(dt.getHours()).padStart(2, '0');
        const minutes = String(dt.getMinutes()).padStart(2, '0');
        setScheduleTime(`${hours}:${minutes}`);
      }
    }

    // Restore audience selection — check cadence-level savedSearchId AND audiences array
    const searchAudience = existingCadence.audiences?.find((a) => a.audienceType === 'saved_search');
    const listAudience = existingCadence.audiences?.find((a) => a.audienceType === 'list');
    const ssId = existingCadence.savedSearchTagId || searchAudience?.savedSearchTagId;

    const individualAudiences = (existingCadence.audiences || []).filter((a) => a.audienceType === 'individual');

    if (ssId) {
      setSelectionMethod(SELECTION_METHOD_OPTIONS.find((o) => o.value === 'search') || SELECTION_METHOD_OPTIONS[0]);
      const match = savedSearches.find((s) => s.value === String(ssId));
      setSelectedSavedSearch(match || { value: String(ssId), label: 'Saved Search' });
    } else if (listAudience?.contactListId) {
      setSelectionMethod(SELECTION_METHOD_OPTIONS.find((o) => o.value === 'list') || SELECTION_METHOD_OPTIONS[0]);
      const match = contactLists.find((l) => String(l.value) === String(listAudience.contactListId));
      setSelectedList(
        match || { label: `List #${listAudience.contactListId}`, value: String(listAudience.contactListId) }
      );
    } else if (individualAudiences.length > 0) {
      setSelectionMethod(SELECTION_METHOD_OPTIONS.find((o) => o.value === 'contacts') || SELECTION_METHOD_OPTIONS[0]);
      setSelectedContacts(
        individualAudiences.map((a) => ({ id: a.contactId, name: `Contact #${a.contactId}`, email: null }))
      );
    }

    // Restore linked blueprint
    if (existingCadence.sourceCadenceTemplateId) {
      const tid = String(existingCadence.sourceCadenceTemplateId);
      setCadenceTemplate(tid);
      const shell = cadenceShells.find((s) => String(s.id) === tid);
      if (shell) setTemplateSearch(shell.name);
    }

    // Restore blackout dates setting from existing cadence (once gateway has the field)
    if (existingCadence?.respectBlackoutDates != null) {
      setRespectBlackoutDates(existingCadence.respectBlackoutDates);
    }

    // Load sharing settings asynchronously
    if (cadenceId) {
      fetchCadenceSharingSettings(cadenceId)
        .then((sharingSettings) => {
          if (sharingSettings?.length > 0) {
            const userShares = sharingSettings
              .filter((s) => s.userId !== 0)
              .map((s) => ({
                shareId: s.id,
                userId: s.userId,
                permission: s.canEdit ? 'edit' : 'view',
                label: `User ${s.userId}`,
              }));
            if (userShares.length > 0) {
              setSharedUsers(userShares);
              setOriginalSharedUsers(userShares);
              // Resolve user IDs to actual names
              fetchUsersById(userShares.map((u) => u.userId))
                .then((users) => {
                  const nameMap = {};
                  (users || []).forEach((u) => {
                    nameMap[u.id] = u.name || u.email || `User ${u.id}`;
                  });
                  const resolved = userShares.map((u) => ({
                    ...u,
                    label: nameMap[u.userId] || u.label,
                  }));
                  setSharedUsers(resolved);
                  setOriginalSharedUsers(resolved);
                })
                .catch((e) => console.warn('[cadences] background fetch failed:', e.message));
            }
          }
        })
        .catch((err) => {
          console.warn('[cadences] Failed to load sharing settings:', err.message);
        });
    }

    setPrePopulated(true);
    // Mark as "saved" so close-button doesn't falsely show the leave-confirmation modal
    if (!lastSavedAt) setLastSavedAt(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCadence, prePopulated]);

  // Seed senderEmailPrefix from the user's first.last name, falling back to the org's default
  // sending address. Only sets if empty (new message) — existing messages are pre-populated above.
  useEffect(() => {
    setSenderEmailPrefix((prev) => {
      if (prev) return prev;
      const fromParts = [user?.first_name, user?.last_name]
        .filter(Boolean)
        .join('.')
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '');
      if (fromParts) return fromParts;
      const nameParts = (user?.name || '').trim().split(/\s+/);
      const fromName = nameParts
        .slice(0, 2)
        .filter(Boolean)
        .join('.')
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, '');
      if (fromName) return fromName;
      const defaultEmail = orgDomainObj?.default_sending_email;
      if (defaultEmail) {
        const atIdx = defaultEmail.indexOf('@');
        return atIdx >= 0 ? defaultEmail.slice(0, atIdx) : prev;
      }
      return prev;
    });
  }, [user?.first_name, user?.last_name, user?.name, orgDomainObj?.default_sending_email]);

  // When creating from a bulk message blueprint, copy blueprint content into new message
  useEffect(() => {
    if (!preSelectedShellId || cadenceId || shellLoaded) return;
    setShellLoaded(true);
    cadenceGqlCall(CADENCE_QUERY, { id: String(preSelectedShellId) })
      .then((data) => {
        const shell = data?.cadence;
        if (!shell) return;
        const emailEvent = (shell.events || []).find((e) => (e.eventType || '').toLowerCase() === 'email');
        if (emailEvent?.eventConfig) {
          const cfg = emailEvent.eventConfig;
          if (cfg.from_name) setSenderName(cfg.from_name);
          if (cfg.from_email) {
            const atIdx = cfg.from_email.indexOf('@');
            if (atIdx >= 0) setSenderEmailPrefix(cfg.from_email.slice(0, atIdx));
            else setSenderEmailPrefix(cfg.from_email);
          }
          const cfgReplyTo =
            (Array.isArray(cfg.reply_to_emails) ? cfg.reply_to_emails.join(', ') : null) ||
            cfg.reply_to_email ||
            cfg.reply_to;
          if (cfgReplyTo) setReplyToEmail(cfgReplyTo);
          if (cfg.subject) setSubject(cfg.subject);
          const htmlBody = cfg.html_body || cfg.htmlBody;
          if (htmlBody) setBodyText(htmlBody);
          if (cfg.signature_id)
            setAppliedSignature({ id: cfg.signature_id, signature_name: cfg.signature_name || 'Signature' });
          if (cfg.template_name || emailEvent.emailTemplateId) {
            setAppliedTemplate({ id: emailEvent.emailTemplateId, templateName: cfg.template_name || 'Template' });
          }
        }
        // Copy success metrics from blueprint
        if (shell.successMetrics?.length > 0) {
          const m = shell.successMetrics[0];
          const raw = m.metricConfig || {};
          const config = {
            ...raw,
            teamId: raw.teamId || raw.team_id || null,
            teamName: raw.teamName || raw.team_name || null,
            stageId: raw.stageId || raw.stage_id || null,
            stageName: raw.stageName || raw.stage_name || null,
            savedSearchId: raw.savedSearchId || raw.saved_search_id || null,
            savedSearchName: raw.savedSearchName || raw.saved_search_name || null,
            interactionType: raw.interactionType || raw.interaction_type || null,
            minAmount: raw.minAmount || raw.min_amount || null,
          };
          // Don't carry over blueprint metric ID — this is a new cadence
          setSuccessMetric({ type: m.metricType, targetPercentage: m.targetPercentage || 0, config });
        }
        // Copy tags from blueprint (don't use blueprint's linked cadence template — set to the blueprint itself)
        if (shell.tags?.length > 0) setTags(shell.tags);
        // Copy blackout dates setting
        if (shell.respectBlackoutDates != null) setRespectBlackoutDates(shell.respectBlackoutDates);
        // Copy linked proposals from blueprint
        if (shell.linkedProposals?.length > 0) {
          const restored = shell.linkedProposals.map((lp) => ({
            value: String(lp.proposalId),
            label: `Proposal #${lp.proposalId}`,
          }));
          setLinkedProposals(restored);
        }
        // Copy sharing settings from blueprint
        fetchCadenceSharingSettings(String(preSelectedShellId))
          .then((sharingSettings) => {
            if (!sharingSettings?.length) return;
            const userShares = sharingSettings
              .filter((s) => s.userId !== 0)
              .map((s) => ({
                shareId: s.id,
                userId: s.userId,
                permission: s.canEdit ? 'edit' : 'view',
                label: `User ${s.userId}`,
              }));
            if (userShares.length > 0) {
              setSharedUsers(userShares);
              setOriginalSharedUsers(userShares);
              fetchUsersById(userShares.map((u) => u.userId))
                .then((users) => {
                  const nameMap = {};
                  (users || []).forEach((u) => {
                    nameMap[u.id] = u.name || u.email || `User ${u.id}`;
                  });
                  const resolved = userShares.map((u) => ({
                    ...u,
                    label: nameMap[u.userId] || u.label,
                  }));
                  setSharedUsers(resolved);
                  setOriginalSharedUsers(resolved);
                })
                .catch((e) => console.warn('[cadences] blueprint share user name fetch failed:', e.message));
            }
          })
          .catch((err) => console.warn('[cadences] Failed to load blueprint sharing settings:', err.message));
      })
      .catch((err) => console.warn('[cadences] Failed to load blueprint content:', err.message));
  }, [preSelectedShellId, cadenceId, shellLoaded, fetchCadenceSharingSettings]);

  // Enrich saved search label once the saved searches list loads
  useEffect(() => {
    if (!selectedSavedSearch || savedSearches.length === 0) return;
    const match = savedSearches.find((s) => String(s.value) === String(selectedSavedSearch.value));
    if (match && selectedSavedSearch.label !== match.label) {
      setSelectedSavedSearch(match);
    }
  }, [savedSearches, selectedSavedSearch]);

  // Enrich list label once the contact lists load (race condition fix)
  useEffect(() => {
    const listAudience = existingCadence?.audiences?.find((a) => a.audienceType === 'list');
    if (listAudience?.contactListId && contactLists?.length > 0 && selectedList) {
      const match = contactLists.find((l) => String(l.value) === String(listAudience.contactListId));
      if (match && selectedList.label !== match.label) {
        setSelectedList(match);
      }
    }
  }, [contactLists, existingCadence?.audiences]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-resolve blueprint name when cadenceShells finishes loading (race condition fix)
  useEffect(() => {
    if (existingCadence?.sourceCadenceTemplateId && cadenceShells?.length > 0) {
      const shell = cadenceShells.find((s) => String(s.id) === String(existingCadence.sourceCadenceTemplateId));
      if (shell) {
        setCadenceTemplate(String(shell.id));
        setTemplateSearch(shell.name);
      }
    }
  }, [cadenceShells?.length, existingCadence?.sourceCadenceTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch contact count when a list is selected (uses search API like the list page)
  useEffect(() => {
    if (!selectedList?.value) return;
    Api.CONTACTS.SEARCH.post({
      params: { offset: 0, limit: 0 },
      data: JSON.stringify({ must: [{ 'list_memberships.list_id': { match: String(selectedList.value) } }] }),
    })
      .then((resp) => {
        const total = resp?.total ?? 0;
        if (total !== selectedList.count) {
          setSelectedList((prev) => (prev?.value === selectedList.value ? { ...prev, count: total } : prev));
        }
      })
      .catch((e) => console.warn('[cadences] background fetch failed:', e.message));
  }, [selectedList?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateAway = () => {
    EverTrue.Navigator('/cadences-v2', true);
  };

  /** Close button handler — confirm if there are unsaved changes. */
  const handleClose = () => {
    const hasUnsavedChanges = !lastSavedAt && (messageName !== bulkDefaultName || subject.trim() || bodyText.trim());
    if (hasUnsavedChanges) {
      setShowLeaveConfirm(true);
    } else {
      navigateAway();
    }
  };

  /** Fetch tag autocomplete suggestions from org-wide registry, debounced 300ms. */
  useEffect(() => {
    if (!tagInput.trim()) {
      setTagSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      cadenceGqlCall(ORG_TAGS_QUERY, { search: tagInput.trim() })
        .then((data) => {
          const existingLower = tags.map((t) => t.toLowerCase());
          const suggestions = (data?.orgTags || []).filter((t) => !existingLower.includes(t.toLowerCase()));
          setTagSuggestions(suggestions);
        })
        .catch(() => setTagSuggestions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [tagInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectTagSuggestion = (tag) => {
    const normalized = tag.toLowerCase();
    if (!tags.map((t) => t.toLowerCase()).includes(normalized)) {
      setTags([...tags, normalized]);
    }
    setTagInput('');
    setTagSuggestions([]);
  };

  const handleAddTag = () => {
    const existingLower = tags.map((t) => t.toLowerCase());
    const newTags = tagInput
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t && !existingLower.includes(t));
    if (newTags.length > 0) {
      setTags([...tags, ...newTags]);
      newTags.forEach((t) => cadenceGqlCall(REGISTER_TAG_MUTATION, { name: t }).catch(() => {}));
    }
    setTagInput('');
    setTagSuggestions([]);
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(3, prev + 1));
  };

  /** Build sharing input for createCadenceWithConfig / updateCadenceWithConfig. */
  const buildSharingInput = () => {
    const shares = [];
    for (const share of sharedUsers) {
      const canEdit = share.permission === 'edit';
      shares.push({
        userId: Number(share.userId),
        canView: true,
        canEdit,
        shareLevel: canEdit ? 'editable' : 'viewable',
      });
    }
    return shares;
  };

  /** Build success metrics input for createCadenceWithConfig / updateCadenceWithConfig. */
  const buildSuccessMetricsInput = () => {
    if (!successMetric?.type) return [];
    const mc = buildMetricConfig(successMetric);
    return [
      {
        metricType: successMetric.type,
        targetPercentage: successMetric.targetPercentage ?? null,
        metricConfig: mc && Object.keys(mc).length > 0 ? mc : null,
        priority: 0,
      },
    ];
  };

  /** Check if name is still the default — if so, show prompt before proceeding. */
  const requireName = (action) => {
    if (messageName === bulkDefaultName || messageName.startsWith('Untitled') || !messageName.trim()) {
      setNamePromptValue(messageName);
      setPendingAction(() => action);
      setNamePromptOpen(true);
    } else {
      action(messageName);
    }
  };

  const handleNamePromptConfirm = () => {
    const name = namePromptValue.trim() || bulkDefaultName;
    setMessageName(name);
    setNamePromptOpen(false);
    const action = pendingAction;
    setPendingAction(null);
    if (action) action(name);
  };

  const handleNamePromptCancel = () => {
    setNamePromptOpen(false);
    setPendingAction(null);
  };

  /** Enroll recipients based on the current selection method. Returns true on success. */
  const enrollRecipients = async (cadenceId) => {
    const method = selectionMethod?.value;
    try {
      if (method === 'contacts' && selectedContacts.length > 0) {
        const contactIds = selectedContacts.map((c) => c.id);
        // Persist audience rows so the selection survives draft saves
        await saveIndividualCadenceAudiences(cadenceId, contactIds);
        const result = await bulkEnrollMembers(cadenceId, contactIds);
        if (result?.success) {
          const count = result.job?.totalItems || contactIds.length;
          EverTrue.Alert.info(`Enrolling ${count} contact(s) in the background`);
        } else {
          EverTrue.Alert.warn(result?.message || 'Some contacts may not have been enrolled');
        }
      } else if (method === 'list' && selectedList) {
        const result = await createCadenceListAudience(cadenceId, selectedList.value);
        if (result?.success) {
          EverTrue.Alert.info(`Importing contacts from list "${selectedList.label}"`);
        } else {
          EverTrue.Alert.warn(result?.message || 'Failed to attach list audience');
        }
      } else if (method === 'search' && selectedSavedSearch) {
        // Dynamic lists have a tag_id that the Tags API uses, which differs from the
        // contacts list id. useSavedSearches() now carries both — value=tag_id and
        // listId=contacts list id (the latter powers the saved-search refresh workflow).
        const result = await createCadenceAudience(cadenceId, selectedSavedSearch.value, selectedSavedSearch.listId);
        if (result?.success) {
          EverTrue.Alert.info(`Importing contacts from saved search "${selectedSavedSearch.label}"`);
        } else {
          EverTrue.Alert.warn(result?.message || 'Failed to attach saved search audience');
        }
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to add recipients: ${err.message}`);
    }
  };

  /** Validates email fields before saving. Returns true if valid. */
  const composedSenderEmail = senderEmailPrefix.trim() ? `${senderEmailPrefix.trim()}@${orgDomain}` : '';

  const validateEmailContent = () => {
    const missing = [];
    if (!senderEmailPrefix.trim()) missing.push('sender email');
    if (!subject.trim()) missing.push('subject line');
    if (!bodyText.trim()) missing.push('email body');
    if (missing.length > 0) {
      EverTrue.Alert.error(`Please fill in the following before saving: ${missing.join(', ')}`);
      if (currentStep !== 1) setCurrentStep(1);
      return false;
    }
    if (senderEmailPrefix.includes('@')) {
      EverTrue.Alert.error('Sender email must not include "@" or a domain — enter only the part before the @.');
      if (currentStep !== 1) setCurrentStep(1);
      return false;
    }
    if (successMetric?.type && successMetric.targetPercentage === 0) {
      EverTrue.Alert.error('Success metric target percentage must be at least 1%. Please update it before sending.');
      return false;
    }
    return true;
  };

  /** Build the metric_config object from the current successMetric state. */
  const buildMetricConfig = (sm) => {
    if (!sm?.type) return null;
    const cfg = sm.config || {};
    const mc = {};
    if (sm.type === 'interaction_created' && cfg.interactionType) mc.interaction_type = cfg.interactionType;
    if (sm.type === 'gift_given') {
      if (cfg.minAmount) mc.min_amount = Number(String(cfg.minAmount).replace(/,/g, ''));
      if (cfg.designation) mc.designation = cfg.designation;
    }
    if (sm.type === 'portfolio_stage_reached') {
      if (cfg.teamId) mc.team_id = cfg.teamId;
      if (cfg.teamName) mc.team_name = cfg.teamName;
      if (cfg.stageId) mc.stage_id = cfg.stageId;
      if (cfg.stageName) mc.stage_name = cfg.stageName;
    }
    if (sm.type === 'saved_search') {
      if (cfg.savedSearchId) {
        mc.saved_search_id = cfg.savedSearchId;
        // saved_search_tag_id is what the cadences backend's index reads from
        // metric_config — the picker's `value` is the tag id, so this maps.
        mc.saved_search_tag_id = cfg.savedSearchId;
      }
      if (cfg.savedSearchName) mc.saved_search_name = cfg.savedSearchName;
      // List id powers the saved-search refresh workflow.
      if (cfg.savedSearchListId != null) mc.saved_search_list_id = Number(cfg.savedSearchListId);
    }
    if (['gifts_amount', 'gifts_count', 'meetings_count'].includes(sm.type)) {
      if (cfg.threshold) mc.threshold = Number(cfg.threshold);
    }
    return Object.keys(mc).length > 0 ? mc : null;
  };

  const handleDisconnectAudience = async (audienceId) => {
    try {
      const result = await deleteCadenceAudience(audienceId);
      if (result?.success) refetchCadence();
      else EverTrue.Alert.error(result?.message || 'Failed to disconnect audience');
    } catch (err) {
      EverTrue.Alert.error(`Failed to disconnect: ${err.message}`);
    }
  };

  const handleDisconnectAllManual = async (individualAudiences) => {
    try {
      for (const audience of individualAudiences) {
        await deleteCadenceAudience(audience.id);
      }
      refetchCadence();
      EverTrue.Alert.success('Removed all manually added contacts');
    } catch (err) {
      EverTrue.Alert.error(`Failed to remove contacts: ${err.message}`);
    }
  };

  const canEditRecipients = !existingCadence || !!existingCadence.canDelete;
  const isTemplateMode = !!existingCadence?.isTemplate;

  const toScheduledSendDate = (date, time) => {
    if (!time) throw new Error('Please select a send time before scheduling.');
    const d = new Date(`${date}T${time}:00`);
    if (isNaN(d.getTime())) throw new Error('Invalid scheduled date. Please select a valid date and try again.');
    if (d <= new Date()) throw new Error('Scheduled time must be in the future.');
    return d.toISOString().replace(/\.\d{3}Z$/, '+00:00');
  };

  const buildEmailEventConfig = () => ({
    from_email: composedSenderEmail,
    from_name: senderName || undefined,
    reply_to_emails: replyToEmail
      ? replyToEmail
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined,
    subject,
    html_body: bodyText,
    email_template_id: appliedTemplate?.id || null,
    template_name: appliedTemplate?.templateName || null,
    signature_id: appliedSignature?.id || null,
    signature_name: appliedSignature?.signature_name || null,
    attachments: filesToEventConfigAttachments(files),
  });

  const handleSaveDraft = async (nameOverride) => {
    if (isSaving || savingRef.current || !validateEmailContent()) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const saveName = nameOverride || messageName;
      const eventConfig = buildEmailEventConfig();

      if (cadenceId && existingCadence) {
        // Edit mode: atomic update of cadence + event + metrics + sharing + proposals.
        // Only owners (canDelete) may change the scheduled send date.
        const isOwnerLevel = !!existingCadence?.canDelete;
        const draftSchedule =
          isOwnerLevel && scheduleType === 'later' && scheduleDate
            ? toScheduledSendDate(scheduleDate, scheduleTime)
            : undefined;
        const existingEmailEvent = (existingCadence.events || []).find(
          (e) => (e.eventType || '').toLowerCase() === 'email'
        );
        const draftResult = await cadenceGqlCall(UPDATE_CADENCE_WITH_CONFIG_MUTATION, {
          cadenceId: String(cadenceId),
          version: getVersion(),
          input: {
            cadence: {
              name: saveName,
              tags,
              sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
              respectBlackoutDates,
              senderEmail: composedSenderEmail || undefined,
              senderName: senderName || undefined,
              replyToEmails: replyToEmail
                ? replyToEmail
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                : undefined,
              ...(draftSchedule
                ? {
                    scheduledSendDate: draftSchedule,
                    scheduledSendTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  }
                : {}),
            },
            events: [
              {
                eventType: 'EMAIL',
                isAutomated: true,
                sequenceOrder: 1,
                eventConfig,
                referenceId: existingEmailEvent?.referenceId || null,
              },
            ],
            branches: [],
            successMetrics: buildSuccessMetricsInput(),
            sharing: buildSharingInput(),
            proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
          },
        });
        const draftUpdate = draftResult?.updateCadenceWithConfig;
        if (!draftUpdate?.success) throw new Error(draftUpdate?.message || 'Failed to update bulk message');
        if (draftUpdate.version) trackVersion(draftUpdate.version);

        // Audience management — not part of config, handled separately.
        // Enroll new audience before deleting old to ensure the cadence always has a valid
        // audience if creation succeeds, even if old-row cleanup fails.
        if (canEditRecipients) {
          const method = selectionMethod?.value;
          const existingSearchAudience = (existingCadence.audiences || []).find(
            (a) => a.audienceType === 'saved_search'
          );
          const existingListAudience = (existingCadence.audiences || []).find((a) => a.audienceType === 'list');
          const existingSearchId = existingCadence.savedSearchTagId ? String(existingCadence.savedSearchTagId) : '';
          const existingListId = existingListAudience?.contactListId ? String(existingListAudience.contactListId) : '';
          const audienceChanged =
            (method === 'search' && selectedSavedSearch?.value && selectedSavedSearch.value !== existingSearchId) ||
            (method === 'list' && selectedList?.value && selectedList.value !== existingListId) ||
            (method === 'contacts' && selectedContacts.length > 0);
          // Only enroll on draft — never trigger enrollment on an active/scheduled/sent cadence
          // from the draft-save path, as that would fire emails immediately.
          const draftSaveStatus = existingCadence?.status?.toLowerCase();
          if (audienceChanged && draftSaveStatus === 'draft') {
            await enrollRecipients(cadenceId);
          }
          if (existingSearchAudience && (method !== 'search' || !selectedSavedSearch?.value)) {
            try {
              await deleteCadenceAudience(existingSearchAudience.id);
            } catch (err) {
              EverTrue.Alert.warn(
                'Failed to remove the previous audience configuration. It may still appear in settings.'
              );
            }
          }
          if (existingListAudience && (method !== 'list' || !selectedList?.value)) {
            try {
              await deleteCadenceAudience(existingListAudience.id);
            } catch (err) {
              EverTrue.Alert.warn(
                'Failed to remove the previous audience configuration. It may still appear in settings.'
              );
            }
          }
        }

        setLastSavedAt(new Date());
        EverTrue.Alert.success('Bulk message saved');
      } else {
        // Create mode: atomic create of cadence + event + metrics + sharing + proposals.
        const createResult = await cadenceGqlCall(CREATE_CADENCE_WITH_CONFIG_MUTATION, {
          input: {
            cadence: {
              name: saveName,
              cadenceType: 'bulk_message',
              status: 'draft',
              tags: tags.length > 0 ? tags : undefined,
              sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
              respectBlackoutDates,
              senderEmail: composedSenderEmail || undefined,
              senderName: senderName || undefined,
              replyToEmails: replyToEmail
                ? replyToEmail
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                : undefined,
              ...(scheduleType === 'later' && scheduleDate
                ? {
                    scheduledSendDate: toScheduledSendDate(scheduleDate, scheduleTime),
                    scheduledSendTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  }
                : {}),
            },
            events: [{ eventType: 'EMAIL', isAutomated: true, sequenceOrder: 1, eventConfig }],
            branches: [],
            successMetrics: buildSuccessMetricsInput(),
            sharing: buildSharingInput(),
            proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
          },
        });
        const created = createResult?.createCadenceWithConfig;
        if (!created?.success) throw new Error(created?.message || 'Failed to create bulk message');
        if (created.cadence?.id) {
          await enrollRecipients(created.cadence.id);
          setLastSavedAt(new Date());
          EverTrue.Alert.success('Bulk message saved as draft');
          EverTrue.Navigator(`/cadences-v2/bulk-message/${created.cadence.id}/edit`, { trigger: true, replace: true });
        }
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to save draft: ${err.message}`);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testRecipient) return;
    if (!subject.trim() || !bodyText.trim()) {
      EverTrue.Alert.error('Please add a subject and body before sending a test.');
      return;
    }
    if (senderEmailPrefix.includes('@')) {
      EverTrue.Alert.error('Sender email must not include "@" or a domain — enter only the part before the @.');
      return;
    }

    const recipientEmail = user?.email;
    if (!recipientEmail) {
      EverTrue.Alert.error('Could not determine your email address.');
      return;
    }

    // Replace personalization fields with test recipient data
    const nameParts = (testRecipient.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
    const replacements = {
      '{{name_first}}': firstName,
      '{{name_last}}': lastName,
      '{{primary_email}}': testRecipient.email || '',
      '{{prefix}}': '',
      '{{name_nick}}': firstName,
      '{{primary_phone}}': '',
      '{{user_name}}': '',
    };

    let personalizedSubject = subject;
    let personalizedBody = bodyText;
    for (const [tag, value] of Object.entries(replacements)) {
      personalizedSubject = personalizedSubject.replaceAll(tag, value);
      personalizedBody = personalizedBody.replaceAll(tag, value);
    }

    // test_send (NEXDEV-846) forces a real delivery through outreach's dedicated
    // test-sends Postmark token, bypassing sandbox/test_org/safe-send routing -
    // without it, a test send from a staging/test_org account only goes out for
    // real when the recipient happens to be on the safe-send allowlist.
    const payload = {
      automated: true,
      test_send: true,
      message: {
        subject: `[TEST] ${personalizedSubject}`,
        body: personalizedBody,
        to: [{ email: recipientEmail, name: user?.name || '' }],
        from: { email: composedSenderEmail, name: senderName || undefined },
        attachment_requests: buildAttachmentRequests(files),
      },
    };

    EverTrue.Alert.info('Sending test email to your address...');
    try {
      await Api.OUTREACH.SEND.post({ data: JSON.stringify(payload) });
      EverTrue.Alert.success(`Test email sent to ${recipientEmail}`);
    } catch (err) {
      EverTrue.Alert.error('Test send failed. Check that the sender email domain is verified.');
    }
  };

  /**
   * Save settings for a sent (active) bulk message — updates metadata, success metrics,
   * sharing, and proposals atomically without touching email content or status.
   */
  const handleSaveSettings = async () => {
    if (isSaving || savingRef.current) return;
    if (!messageName.trim()) {
      EverTrue.Alert.error('Please enter a name before saving.');
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    try {
      const settingsResult = await cadenceGqlCall(UPDATE_CADENCE_WITH_CONFIG_MUTATION, {
        cadenceId: String(cadenceId),
        version: getVersion(),
        input: {
          cadence: {
            name: messageName,
            tags,
            sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
            respectBlackoutDates,
          },
          events: null,
          branches: null,
          successMetrics: buildSuccessMetricsInput(),
          sharing: buildSharingInput(),
          proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
        },
      });
      const settingsUpdate = settingsResult?.updateCadenceWithConfig;
      if (!settingsUpdate?.success) throw new Error(settingsUpdate?.message || 'Failed to save settings');
      if (settingsUpdate.version) trackVersion(settingsUpdate.version);
      setLastSavedAt(new Date());
      EverTrue.Alert.success('Settings saved');
    } catch (err) {
      EverTrue.Alert.error(`Failed to save settings: ${err.message}`);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleConfirmSend = async (nameOverride) => {
    if (isSaving || savingRef.current || !validateEmailContent()) return;
    if (scheduleType === 'later' && !scheduleDate) {
      EverTrue.Alert.error('Please select a date for scheduled send, or choose "Send now".');
      return;
    }
    const configIssues = findIncompleteConfigs({ successMetric });
    if (configIssues.length > 0) {
      EverTrue.Alert.error(`Cannot send — ${configIssues.join('; ')}.`);
      return;
    }
    if (domainStatus === 'not_configured') {
      EverTrue.Alert.error(
        'Email sending requires a verified domain. Ask your organization Owner to configure a sending domain in Organization Settings.'
      );
      return;
    }
    if (domainStatus === 'pending') {
      EverTrue.Alert.error(
        "Your organization's sending domain is not yet verified. DNS verification can take up to 48 hours. Try again once verification is complete."
      );
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    try {
      const saveName = nameOverride || messageName;
      const eventConfig = buildEmailEventConfig();
      // Convert user's local datetime to a UTC ISO string so the backend can compare
      // it correctly against workflow.now() (which is also UTC). Sending a naive
      // local datetime caused the backend to assume UTC, making scheduled messages
      // fire immediately for users in negative-offset timezones.
      const scheduledSendDate =
        scheduleType === 'later' && scheduleDate ? toScheduledSendDate(scheduleDate, scheduleTime) : undefined;

      if (cadenceId && existingCadence) {
        // Edit mode: atomic update then enroll recipients.
        const existingEmailEvt = (existingCadence.events || []).find(
          (e) => (e.eventType || '').toLowerCase() === 'email'
        );
        const sendResult = await cadenceGqlCall(UPDATE_CADENCE_WITH_CONFIG_MUTATION, {
          cadenceId: String(cadenceId),
          version: getVersion(),
          input: {
            cadence: {
              name: saveName,
              status: 'active',
              ...(scheduledSendDate
                ? { scheduledSendDate, scheduledSendTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
                : {}),
              tags,
              sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
              respectBlackoutDates,
            },
            events: [
              {
                eventType: 'EMAIL',
                isAutomated: true,
                sequenceOrder: 1,
                eventConfig,
                referenceId: existingEmailEvt?.referenceId || null,
              },
            ],
            branches: [],
            successMetrics: buildSuccessMetricsInput(),
            sharing: buildSharingInput(),
            proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
          },
        });
        const sendUpdate = sendResult?.updateCadenceWithConfig;
        if (!sendUpdate?.success)
          throw new Error(sendUpdate?.message || 'Failed to update bulk message before sending');
        if (sendUpdate.version) trackVersion(sendUpdate.version);

        // Enroll any new recipients added since the last draft save.
        // Also enroll when confirming a previously-DRAFT cadence for the first time:
        // draft saves store the audience intent but the backend blocks actual member
        // creation (enrollMember rejects DRAFT status), so this is the first chance
        // to actually enroll when the cadence transitions to ACTIVE/SCHEDULED.
        const existingSearchId = existingCadence.savedSearchTagId ? String(existingCadence.savedSearchTagId) : '';
        const existingListAudience = (existingCadence.audiences || []).find((a) => a.audienceType === 'list');
        const existingListId = existingListAudience?.contactListId ? String(existingListAudience.contactListId) : '';
        const sendMethod = selectionMethod?.value;
        const audienceChanged =
          (sendMethod === 'search' && selectedSavedSearch?.value && selectedSavedSearch.value !== existingSearchId) ||
          (sendMethod === 'list' && selectedList?.value && selectedList.value !== existingListId) ||
          (sendMethod === 'contacts' && selectedContacts.length > 0);
        const wasDraft = existingCadence?.status?.toLowerCase() === 'draft';
        if (audienceChanged || wasDraft) await enrollRecipients(cadenceId);

        setLastSavedAt(new Date());
        EverTrue.Alert.success('Bulk message scheduled successfully!');
        EverTrue.Navigator(`/cadences-v2/bulk-message/${cadenceId}`, true);
      } else {
        // Create mode: atomic create then enroll recipients.
        const sendCreateResult = await cadenceGqlCall(CREATE_CADENCE_WITH_CONFIG_MUTATION, {
          input: {
            cadence: {
              name: saveName,
              cadenceType: 'bulk_message',
              status: 'active',
              ...(scheduledSendDate
                ? { scheduledSendDate, scheduledSendTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
                : {}),
              tags: tags.length > 0 ? tags : undefined,
              sourceCadenceTemplateId: cadenceTemplate ? parseInt(cadenceTemplate, 10) : null,
              respectBlackoutDates,
              senderEmail: composedSenderEmail || undefined,
              senderName: senderName || undefined,
              replyToEmails: replyToEmail
                ? replyToEmail
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                : undefined,
            },
            events: [{ eventType: 'EMAIL', isAutomated: true, sequenceOrder: 1, eventConfig }],
            branches: [],
            successMetrics: buildSuccessMetricsInput(),
            sharing: buildSharingInput(),
            proposalIds: linkedProposals.map((p) => parseInt(p.value, 10)).filter((n) => !isNaN(n)),
          },
        });
        const sendCreated = sendCreateResult?.createCadenceWithConfig;
        if (!sendCreated?.success) throw new Error(sendCreated?.message || 'Failed to create bulk message');
        if (sendCreated.cadence?.id) {
          await enrollRecipients(sendCreated.cadence.id);
          setLastSavedAt(new Date());
          EverTrue.Alert.success('Bulk message scheduled successfully!');
          EverTrue.Navigator(`/cadences-v2/bulk-message/${sendCreated.cadence.id}`, true);
        }
      }
    } catch (err) {
      if (err.code === 'ATTACHMENT_TOO_LARGE') {
        EverTrue.Alert.error(
          'Publishing Failed: The bulk message includes an attachment that is over the 7MB limit. Please remove / reduce the size of this attachment in order to publish or send.'
        );
      } else {
        EverTrue.Alert.error(`Failed to send: ${err.message}`);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const getStatusLabel = () => {
    if (cadenceId && existingCadence) return existingCadence.displayStatusLabel || 'Draft';
    return 'Unsaved';
  };

  return (
    <div className="bulk-message-wizard">
      {/* Page Header */}
      <div className="bulk-message-wizard--header">
        <div className="bulk-message-wizard--header-left">
          {isEditingName ? (
            <input
              type="text"
              className="bulk-message-wizard--name-input"
              value={messageName}
              onChange={(e) => setMessageName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsEditingName(false)}
              autoFocus
            />
          ) : (
            <h1 className="bulk-message-wizard--title">
              {messageName}
              <button
                type="button"
                className="bulk-message-wizard--edit-btn"
                aria-label="Edit message name"
                onClick={() => setIsEditingName(true)}
              >
                <Icon icon="edit" />
              </button>
            </h1>
          )}
          <span className="bulk-message-wizard--last-saved">{getStatusLabel()}</span>
        </div>
        <div className="bulk-message-wizard--header-right">
          {existingCadence?.displayStatusLabel &&
          !['Draft', 'Scheduled'].includes(existingCadence.displayStatusLabel) ? (
            <>
              <Button
                type="secondary"
                onClick={() => EverTrue.Navigator(`/cadences-v2/bulk-message/${cadenceId}`, true)}
              >
                <Icon icon="chevron-left" /> Back to Detail
              </Button>
              <Button type="action" onClick={handleSaveSettings} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </>
          ) : (
            <>
              {currentStep > 1 && (
                <Button type="secondary" onClick={handlePrevStep}>
                  <Icon icon="chevron-left" /> Previous
                </Button>
              )}
              <Button type="secondary" onClick={() => requireName(handleSaveDraft)} disabled={isSaving}>
                {isSaving ? 'Saving...' : !cadenceId ? 'Save as Draft' : 'Save'}
              </Button>
              {currentStep < 3 && (
                <Button type="action" onClick={handleNextStep} disabled={isSaving}>
                  Next Step
                </Button>
              )}
              {currentStep === 3 && !isTemplateMode && (!existingCadence || existingCadence.canDelete) && (
                <Button
                  type="action"
                  onClick={() => requireName(() => setShowSendConfirm(true))}
                  disabled={isSaving || domainLoading}
                >
                  <Icon icon="send" />{' '}
                  {isSaving ? 'Sending...' : scheduleType === 'later' ? 'Schedule Send' : 'Send Now'}
                </Button>
              )}
            </>
          )}
          <button type="button" className="create-cadence-page--close-btn" aria-label="Close" onClick={handleClose}>
            <Icon icon="close" />
          </button>
        </div>
      </div>

      {existingCadence?.displayStatusLabel && !['Draft', 'Scheduled'].includes(existingCadence.displayStatusLabel) && (
        <div className="create-cadence-page--readonly-banner">
          <Icon icon="info" />
          <span>
            This message has been sent. The email content cannot be changed, but you can still update success metrics
            and advanced settings. Use{' '}
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit',
              }}
              onClick={() => EverTrue.Navigator(`/cadences-v2/bulk-message/${cadenceId}`, true)}
            >
              the detail page
            </button>{' '}
            to view results.
          </span>
        </div>
      )}

      {/* Step Indicator */}
      <BulkMessageStepIndicator currentStep={currentStep} onStepClick={setCurrentStep} />

      {/* Step Content */}
      <div className="bulk-message-wizard--content">
        {(domainStatus === 'not_configured' || domainStatus === 'pending') && (
          <div className="bulk-message--domain-warning">
            <div className="bulk-message--domain-warning-title">
              <Icon icon="warning" />
              <strong>Sending Unavailable — Domain Not Verified</strong>
            </div>
            <p className="bulk-message--domain-warning-body">
              {domainStatus === 'not_configured' ? (
                <>
                  Your organization hasn&apos;t configured a custom sending domain. This is separate from your personal
                  email outreach account — it&apos;s an org-wide DNS setting that only an EverTrue Owner can configure
                  on the{' '}
                  {isOrgAdmin ? (
                    <a href="/settings/org_settings">organization settings page</a>
                  ) : (
                    'organization settings page'
                  )}
                  .
                </>
              ) : (
                <>
                  Your organization&apos;s domain verification is in progress (up to 48 hours). Bulk messages cannot be
                  sent or scheduled until verification is complete.{' '}
                  {isOrgAdmin ? (
                    <a href="/settings/org_settings">Check status in Org Settings →</a>
                  ) : (
                    'Check with your organization Owner for status updates.'
                  )}
                </>
              )}
            </p>
          </div>
        )}
        {currentStep === 1 && (
          <BulkMessageContentStep
            senderExpanded={senderExpanded}
            setSenderExpanded={setSenderExpanded}
            senderEmailPrefix={senderEmailPrefix}
            setSenderEmailPrefix={setSenderEmailPrefix}
            orgDomain={orgDomain}
            senderName={senderName}
            setSenderName={setSenderName}
            replyToEmail={replyToEmail}
            setReplyToEmail={setReplyToEmail}
            contentExpanded={contentExpanded}
            setContentExpanded={setContentExpanded}
            subject={subject}
            setSubject={setSubject}
            bodyText={bodyText}
            setBodyText={setBodyText}
            setAppliedTemplate={setAppliedTemplate}
            files={files}
            removeFile={removeFile}
            onImageUpload={onImageUpload}
            onFileUpload={onFileUpload}
            outreachTemplates={outreachTemplates}
            prePopulated={prePopulated}
            previewMode={previewMode}
            setPreviewMode={setPreviewMode}
            appliedSignature={appliedSignature}
          />
        )}
        {currentStep === 2 && existingCadence && !canEditRecipients && (
          <div className="create-cadence-page--readonly-banner">
            <Icon icon="lock" />
            <span>Only owners can add or change recipients. You can view the current audience selection below.</span>
          </div>
        )}
        {currentStep === 3 && !isTemplateMode && existingCadence && !existingCadence.canDelete && (
          <div className="create-cadence-page--readonly-banner">
            <Icon icon="lock" />
            <span>Only owners can schedule or send this bulk message.</span>
          </div>
        )}
        {currentStep === 2 && (
          <BulkMessageAudienceStep
            canDelete={canEditRecipients}
            existingAudiences={existingCadence?.audiences || []}
            audiencesLoaded={!audiencesLoading}
            onDisconnect={handleDisconnectAudience}
            onDisconnectAllManual={handleDisconnectAllManual}
            contactLists={contactLists}
            savedSearches={savedSearches}
            selectionMethod={selectionMethod}
            setSelectionMethod={setSelectionMethod}
            selectedList={selectedList}
            setSelectedList={setSelectedList}
            selectedSavedSearch={selectedSavedSearch}
            setSelectedSavedSearch={setSelectedSavedSearch}
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
            successMetric={successMetric}
            setSuccessMetric={setSuccessMetric}
            interactionTypes={interactionTypes}
            portfolioTeams={portfolioTeams}
            isAdvancedOpen={isAdvancedOpen}
            setIsAdvancedOpen={setIsAdvancedOpen}
            isSharingOpen={isSharingOpen}
            setIsSharingOpen={setIsSharingOpen}
            sharedUsers={sharedUsers}
            setSharedUsers={setSharedUsers}
            pendingShareUsers={pendingShareUsers}
            setPendingShareUsers={setPendingShareUsers}
            pendingSharePermission={pendingSharePermission}
            setPendingSharePermission={setPendingSharePermission}
            isProposalsOpen={isProposalsOpen}
            setIsProposalsOpen={setIsProposalsOpen}
            linkedProposals={linkedProposals}
            setLinkedProposals={setLinkedProposals}
            proposals={proposals}
            onProposalSearch={setProposalSearch}
            isBlueprintOpen={isBlueprintOpen}
            setIsBlueprintOpen={setIsBlueprintOpen}
            cadenceTemplate={cadenceTemplate}
            setCadenceTemplate={setCadenceTemplate}
            templateSearch={templateSearch}
            setTemplateSearch={setTemplateSearch}
            templateDropdownOpen={templateDropdownOpen}
            setTemplateDropdownOpen={setTemplateDropdownOpen}
            cadenceShells={cadenceShells}
            isTagsOpen={isTagsOpen}
            setIsTagsOpen={setIsTagsOpen}
            tagInput={tagInput}
            setTagInput={setTagInput}
            tagSuggestions={tagSuggestions}
            tags={tags}
            setTags={setTags}
            handleAddTag={handleAddTag}
            handleSelectTagSuggestion={handleSelectTagSuggestion}
            isBlackoutOpen={isBlackoutOpen}
            setIsBlackoutOpen={setIsBlackoutOpen}
            respectBlackoutDates={respectBlackoutDates}
            setRespectBlackoutDates={setRespectBlackoutDates}
          />
        )}
        {currentStep === 3 && isTemplateMode && (
          <div className="bulk-message-schedule-step">
            <div className="bulk-message-schedule-step--intro">
              <h2 className="bulk-message-schedule-step--title">Schedule &amp; Launch</h2>
              <p className="bulk-message-schedule-step--subtitle">
                When a user creates a bulk message from this blueprint, they will review their message summary here and
                choose when to send.
              </p>
            </div>
          </div>
        )}
        {currentStep === 3 && !isTemplateMode && (
          <BulkMessageScheduleStep
            messageName={messageName}
            subject={subject}
            selectionMethod={selectionMethod}
            selectedList={selectedList}
            selectedSavedSearch={selectedSavedSearch}
            selectedContacts={selectedContacts}
            successMetric={successMetric}
            interactionTypes={interactionTypes}
            portfolioTeams={portfolioTeams}
            sharedUsers={sharedUsers}
            testRecipient={testRecipient}
            setTestRecipient={setTestRecipient}
            testContactSearch={testContactSearch}
            handleSendTest={handleSendTest}
            scheduleType={scheduleType}
            setScheduleType={setScheduleType}
            scheduleDate={scheduleDate}
            setScheduleDate={setScheduleDate}
            scheduleTime={scheduleTime}
            setScheduleTime={setScheduleTime}
          />
        )}
      </div>

      {/* Template Selection Modal */}
      <TemplatePickerModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={outreachTemplates || []}
        onSelect={(template) => {
          setAppliedTemplate(template);
          setSubject(template.templateSubject || '');
          setBodyText(template.templateBody || template.dynamicTemplate || '');
          setShowTemplateModal(false);
        }}
        currentTemplateId={appliedTemplate?.id}
      />

      {/* Signature Selection Modal */}
      <SignaturePickerModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        signatures={signatures || []}
        onSelect={(sig) => {
          setAppliedSignature(sig.id === 0 ? null : sig);
          setShowSignatureModal(false);
        }}
        currentSignatureId={appliedSignature?.id}
      />

      {/* Name Prompt Modal — shown when saving with a default "Untitled" name */}
      <Modal isOpen={namePromptOpen} closeModal={handleNamePromptCancel}>
        <ModalHeader title="Name Your Bulk Message" closeModal={handleNamePromptCancel} />
        <ModalBody>
          <div className="cadence-name-prompt">
            <p className="cadence-name-prompt--text">Give your bulk message a name, or keep the default and save.</p>
            <input
              type="text"
              className="cadence-name-prompt--input"
              value={namePromptValue}
              onChange={(e) => setNamePromptValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNamePromptConfirm();
              }}
              placeholder="Enter bulk message name..."
              autoFocus
            />
            <div className="cadence-name-prompt--actions">
              <Button type="secondary" onClick={handleNamePromptCancel}>
                Cancel
              </Button>
              <Button type="action" onClick={handleNamePromptConfirm}>
                Save
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Send / Schedule Confirmation Modal */}
      <Modal isOpen={showSendConfirm} closeModal={() => setShowSendConfirm(false)}>
        <ModalHeader
          title={scheduleType === 'later' ? 'Schedule This Message?' : 'Send Message Now?'}
          closeModal={() => setShowSendConfirm(false)}
        />
        <ModalBody>
          <div className="cadence-leave-confirm">
            <p>
              {scheduleType === 'later'
                ? `This message will be sent to all selected recipients on ${scheduleDate || 'the scheduled date'}${scheduleTime ? ` at ${scheduleTime}` : ''}. You can cancel or edit it before then.`
                : 'This will begin sending this message to all selected recipients. This cannot be undone.'}
            </p>
            <div className="cadence-leave-confirm--actions">
              <Button type="secondary" onClick={() => setShowSendConfirm(false)}>
                Cancel
              </Button>
              <Button
                type="action"
                onClick={() => {
                  setShowSendConfirm(false);
                  handleConfirmSend(messageName);
                }}
                disabled={isSaving}
              >
                {scheduleType === 'later' ? 'Schedule Send' : 'Send Now'}
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Leave Confirmation Modal */}
      <Modal isOpen={showLeaveConfirm} closeModal={() => setShowLeaveConfirm(false)}>
        <ModalHeader title="Unsaved Changes" closeModal={() => setShowLeaveConfirm(false)} />
        <ModalBody>
          <div className="cadence-leave-confirm">
            <p>Are you sure you want to leave without saving your changes?</p>
            <div className="cadence-leave-confirm--actions">
              <Button type="secondary" onClick={() => setShowLeaveConfirm(false)}>
                Stay
              </Button>
              <Button type="destroy" onClick={navigateAway}>
                Leave
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

CreateBulkMessagePage.propTypes = {
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  preSelectedShellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CreateBulkMessagePage;
